// (C) Copyright 2025 Hewlett Packard Enterprise Development LP

import {
  Anchor,
  Box,
  Button,
  Card,
  CardBody,
  Grid,
  Heading,
  Paragraph,
  Tag,
  Text,
  ThemeContext
} from 'grommet'
import React, { useContext, useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { groupBy } from 'lodash'
import { Apps, AppsRounded, List, StatusCritical } from 'grommet-icons'
import { useNavigate } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'

import {
  useAxiosAuth,
  useRecentServices,
  useNewServiceRedirect as useServiceRedirect,
  useLocalStorage,
  useRegions
} from '../../../dashboard/hooks'
import {
  isMSP,
  isOrgWorkSpace,
  isStagingCluster
} from '../../../utils/feature-flag-utils'
import { getErrorMessage } from '../../../utils/api-utils'
import { localStorageWhitelistWithoutPII } from '../../../utils/local-storage-utils'
import { Loader, Notification, DataTable } from '../../../components'
import {
  DISPLAY_RECENT_SERVICES_LENGTH,
  DATASVS,
  HCIPOC,
  PCAI,
  PRIVATE_CLOUD_BUSINESS
} from '../../../dashboard/constants'
import { CATEGORY } from '../../../utils/constants'
import { isServiceEnabled, saveTimestampToUPS } from '../../../dashboard/utils'
import NewServiceLaunchModal from '../../../dashboard/components/NewServiceLaunchModal'
import VisibilityWrapper from '../../../commoncomponents/visibility-wrapper/VisibilityWrapper'
import { addServicToRecentServices } from '../utils/recent-services-util'

const NewRecentServices = () => {
  const axios = useAxiosAuth()
  useRegions()
  const [recentServices, setRecentServices, migrationCompleted] =
    useRecentServices()
  const { t, i18n } = useTranslation(['dashboard'])

  const [isLoading, setIsLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)

  const [provisionsOfSelectedService, setProvisionsOfSelectedService] =
    useState([])

  const [recentServicesBySlug, setRecentServicesBySlug] = useState({})
  const [errorMessage, setErrorMessage] = useState('')

  const serviceRedirect = useServiceRedirect()
  const { global } = useContext(ThemeContext)
  const LDFlags = useFlags()
  const [recentServicesView, setRecentServicesView] = useLocalStorage(
    localStorageWhitelistWithoutPII.RECENT_SERVICES_VIEW,
    'grid'
  )
  const [upsRecentServicesData, setUpsRecentServicesData] = useState([])
  const upsRecentServicesFlag = LDFlags['glcp-ups-recent-services']
  const saveTimestampToUpsFlag = LDFlags['glcp-ups-save-launch-timestamp']
  const categoryMapper = (categories) =>
    categories[0] ?? CATEGORY.MANAGEMENT_AND_GOVERNANCE
  const headingTitle = t('dashboard.recent_services.title')
  const navigate = useNavigate()

  const dateSort = (a, b) => {
    return Date.parse(b) - Date.parse(a)
  }

  const filterService = (slug) =>
    isServiceEnabled(slug, LDFlags) && (!isOrgWorkSpace() || slug === 'PCE')

  useEffect(() => {
    axios
      .get(
        upsRecentServicesFlag
          ? '/service-catalog/v1beta1/recent-services'
          : '/service-catalog/v1alpha1/my-services',
        {
          params: upsRecentServicesFlag ? {} : { include_omnipresent: true }
        }
      )
      .then(({ data: { items } }) => {
        if (upsRecentServicesFlag) {
          setUpsRecentServicesData(items || [])
        } else {
          setRecentServicesBySlug(
            groupBy(
              items?.flatMap((service) => [
                ...service.provisions.map((region) => ({
                  ...region,
                  regionName: service.region
                }))
              ]),
              'slug'
            ) || {}
          )
        }
      })
      .catch((error) => {
        setErrorMessage(getErrorMessage(error, t))
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [axios, t, setErrorMessage, upsRecentServicesFlag, migrationCompleted])

  const recentServicesData = useMemo(() => {
    const processedRecentServicesBySlug = { ...recentServicesBySlug }
    // adding serviceSlug and category to work with service launch modal
    // change last accessed when available in api
    recentServices.forEach((service) => {
      if (
        service.serviceSlug &&
        service.utc &&
        processedRecentServicesBySlug[service.serviceSlug]
      ) {
        const dateUtc = new Date(service.utc).toISOString()

        processedRecentServicesBySlug[service.serviceSlug][0].utc = dateUtc

        processedRecentServicesBySlug[service.serviceSlug] = [
          ...processedRecentServicesBySlug[service.serviceSlug]
        ]
      }
    })

    // --------------------------------
    // Temp hardcoded snippet for old recent services implementation; Adds PCAI and PCBE
    if (LDFlags['glcp-service-pcai']) {
      const serviceManagerSlug = isStagingCluster() ? HCIPOC : DATASVS
      addServicToRecentServices(
        serviceManagerSlug,
        PCAI,
        'Private Cloud AI',
        processedRecentServicesBySlug,
        recentServices
      )
    }
    addServicToRecentServices(
      DATASVS,
      PRIVATE_CLOUD_BUSINESS,
      'Private Cloud Business Edition',
      processedRecentServicesBySlug,
      recentServices
    )
    // --------------------------------
    const slicedRecentServices = Object.keys(processedRecentServicesBySlug)
      .map((key) => {
        return {
          slug: key,
          date: processedRecentServicesBySlug[key]
            // TODO: wait for new API format for more details
            // after receiving api details update the date
            ?.map(({ utc }) => utc)
            .sort(dateSort)[0]
        }
      })
      .map((value) => {
        return { ...value, date: value?.date ?? new Date(0).toISOString() }
      })
      .sort((a, b) => {
        return Date.parse(b.date) - Date.parse(a.date)
      })
      .slice(0, DISPLAY_RECENT_SERVICES_LENGTH)

    return Object.values(slicedRecentServices)
      .filter((service) => processedRecentServicesBySlug[service?.slug])
      .map((service) => processedRecentServicesBySlug[service?.slug])
  }, [recentServicesBySlug, recentServices, LDFlags])

  const recentServicesDataToUse = upsRecentServicesFlag
    ? upsRecentServicesData?.slice(0, DISPLAY_RECENT_SERVICES_LENGTH)
    : recentServicesData

  const handleLaunch = (staticLaunchUrl, serviceSlug, service) => {
    if (staticLaunchUrl) {
      if (upsRecentServicesFlag && saveTimestampToUpsFlag) {
        saveTimestampToUPS(
          service?.serviceOffer?.id,
          service?.serviceOffer?.name,
          axios
        )
      }
      setRecentServices(serviceSlug)
      return
    }
    if (upsRecentServicesFlag) {
      const provisions = service?.regionalProvisions?.map(
        (regionalProvision) => ({
          slug: service?.serviceOffer?.slug,
          name: service?.serviceOffer?.name,
          categories: service?.serviceOffer?.categories,
          application_provision: {
            ...regionalProvision?.serviceManagerProvision,
            application_instance_id:
              regionalProvision?.serviceManagerProvision
                ?.serviceManagerInstanceId,
            application_customer_id:
              regionalProvision?.serviceManagerProvision?.id ||
              regionalProvision?.serviceProvision?.serviceManagerProvision?.id,
            provision_status:
              regionalProvision?.serviceManagerProvision?.provisionStatus,
            region: regionalProvision?.id
          },
          service_provision: regionalProvision?.serviceProvision
            ? {
                ...regionalProvision?.serviceProvision,
                application_instance_id:
                  regionalProvision?.serviceProvision?.serviceManagerInstanceId,
                application_customer_id:
                  regionalProvision?.serviceProvision?.serviceManagerProvision
                    ?.id || regionalProvision?.serviceManagerProvision?.id,
                region: regionalProvision?.id,
                service_offer_id: service?.serviceOffer?.id,
                provision_status:
                  regionalProvision?.serviceProvision?.provisionStatus
              }
            : null
        })
      )
      if (service?.serviceOffer?.staticLaunchUrl || provisions?.length === 1) {
        serviceRedirect(
          provisions[0]?.service_provision ||
            provisions[0]?.application_provision,
          service.serviceOffer?.id,
          service.serviceOffer?.name,
          service?.serviceOffer?.slug
        )
      } else if (provisions?.length > 1) {
        setProvisionsOfSelectedService(provisions)
        setShowModal(true)
      }
      return
    }

    if (service[0]?.redirectUrl || service.length === 1) {
      serviceRedirect(
        service[0]?.service_provision || service[0]?.application_provision,
        service[0]?.service_offer_id,
        service[0]?.name,
        service[0]?.slug
      )
    } else if (service.length > 1) {
      setProvisionsOfSelectedService(service)
      setShowModal(true)
    }
  }

  const displayServicesView = () =>
    !isLoading &&
    (recentServicesData?.length || upsRecentServicesData?.length) &&
    recentServicesView

  return (
    <VisibilityWrapper
      rbac={{
        resource: '/ccs/service-catalog',
        permission: 'ccs.service-catalog.view'
      }}
    >
      <Box margin={{ bottom: 'large' }}>
        {showModal && (
          <NewServiceLaunchModal
            provisions={provisionsOfSelectedService}
            setShowModal={setShowModal}
          />
        )}
        {(isLoading && (
          <Loader
            testId="service-centric-home-spinner"
            orientation="horizontal"
          />
        )) ||
          (!displayServicesView() ? (
            <Box>
              <Heading level={2}>{headingTitle}</Heading>
              <Box
                margin={{ horizontal: 'auto' }}
                width="large"
                gap="medium"
                align="center"
                data-testid="service-centric-no-data"
              >
                <Box gap="small" align="center">
                  <AppsRounded
                    color={global.colors.green.dark}
                    size="xxlarge"
                  />
                  <Paragraph margin="none" size="large" textAlign="center">
                    {t('dashboard.recent_services.empty')}
                  </Paragraph>
                </Box>
                <Button
                  data-testid="service-catalog-link"
                  onClick={() => navigate('/services/service-catalog')}
                  label={t('dashboard.recent_services.view_catalog')}
                  primary
                />
              </Box>
            </Box>
          ) : (
            <Box>
              {displayServicesView() === 'grid' && (
                <Box data-testid="service-centric-home-grid-view">
                  <Box direction="column" margin={{ bottom: 'medium' }}>
                    <Box
                      align="center"
                      direction="row"
                      margin={{ bottom: 'small' }}
                      justify="between"
                    >
                      <Heading level={2}>{headingTitle}</Heading>
                      <Box direction="row" align="center" gap="small">
                        <Button
                          data-testid="service-centric-list-view-icon"
                          onClick={() => {
                            setRecentServicesView('table')
                          }}
                          icon={<List />}
                          tip={t('dashboard:common.switch_to_list_view')}
                        />
                        <Anchor
                          data-testid="my-service-link-grid"
                          onClick={(e) => {
                            e.preventDefault()
                            navigate('/services/my-services')
                          }}
                          href="/services/my-services"
                        >
                          {t('common.my_services')}
                        </Anchor>
                      </Box>
                    </Box>
                    <Grid
                      pad={{ bottom: 'small' }}
                      gap="small"
                      columns={{
                        count: 'fill',
                        size: ['medium', 'flex']
                      }}
                    >
                      {recentServicesDataToUse
                        .filter(
                          (regions) =>
                            upsRecentServicesFlag ||
                            filterService(regions[0]?.slug)
                        )
                        .map((service) => {
                          const serviceName = upsRecentServicesFlag
                            ? service?.serviceOffer?.name
                            : service[0]?.name
                          const workspaceTypes = upsRecentServicesFlag
                            ? service?.serviceOffer?.workspaceTypes
                            : service[0]?.workspace_types
                          const staticLaunchUrl = upsRecentServicesFlag
                            ? service?.serviceOffer?.staticLaunchUrl
                            : service[0]?.static_launch_url
                          const serviceSlug = upsRecentServicesFlag
                            ? service?.serviceOffer?.slug
                            : service[0]?.slug
                          return (
                            <Card key={`${serviceName}`} title={serviceName}>
                              <CardBody
                                justify="between"
                                direction="row"
                                pad="medium"
                                align="center"
                              >
                                <Box>
                                  <Heading level={3} margin="none">
                                    {serviceName}
                                  </Heading>
                                  <Text size="small">
                                    {t(
                                      `dashboard:common.category.${categoryMapper(
                                        upsRecentServicesFlag
                                          ? service?.serviceOffer?.categories
                                          : service[0]?.categories
                                      )}`
                                    )}
                                  </Text>
                                </Box>
                                {isMSP() &&
                                workspaceTypes?.includes('TENANT') &&
                                !workspaceTypes?.includes('MSP') ? (
                                  <span>
                                    <Tag
                                      value={t(
                                        'dashboard:service_details.enabled_for_customer_workspaces'
                                      )}
                                      align="center"
                                      size="small"
                                      border={{ size: 'small', color: 'brand' }}
                                      color="background"
                                      style={{
                                        whiteSpace: 'nowrap'
                                      }}
                                    />
                                  </span>
                                ) : (
                                  <Button
                                    a11yTitle={`${t(
                                      'common.launch'
                                    )} ${serviceName}`}
                                    href={staticLaunchUrl || null}
                                    label={t('common.launch')}
                                    onClick={() => {
                                      handleLaunch(
                                        staticLaunchUrl,
                                        serviceSlug,
                                        service
                                      )
                                    }}
                                    secondary
                                    data-testid={`recent-launch-${serviceSlug}`}
                                  />
                                )}
                              </CardBody>
                            </Card>
                          )
                        })}
                    </Grid>
                  </Box>
                </Box>
              )}
              {displayServicesView() === 'table' && (
                <Box>
                  <DataTable
                    testId="service-centric-home-list-view"
                    grid={{
                      verticalAlign: 'middle',
                      background: {
                        header: 'white'
                      },
                      border: {
                        body: {
                          color: 'border-weak',
                          side: 'bottom',
                          size: 'xsmall'
                        },
                        header: {
                          color: 'transparent',
                          side: 'bottom',
                          size: 'small'
                        }
                      },
                      pad: {
                        body: { horizontal: 'auto', vertical: 'medium' },
                        header: { horizontal: 'auto' }
                      },
                      columns: [
                        {
                          property: 'title',
                          header: (
                            <Heading level={2} margin="none">
                              {headingTitle}
                            </Heading>
                          ),

                          sortable: false
                        },
                        {
                          property: 'type',
                          header: 'Type',
                          sortable: true
                        },
                        {
                          property: 'last_accessed',
                          header: 'Last Accessed',
                          sortable: true,
                          render: ({ last_accessed }) => {
                            /* Always based on browser/computer settings */
                            const { timeZone } =
                              Intl.DateTimeFormat().resolvedOptions()
                            return last_accessed
                              ? new Date(last_accessed).toLocaleString(
                                  i18n.language,
                                  {
                                    dateStyle: 'medium',
                                    timeZone,
                                    timeStyle: 'short'
                                  }
                                )
                              : ''
                          }
                        },
                        {
                          property: 'path',
                          header: (
                            <Box direction="row" align="center" gap="small">
                              <Button
                                data-testid="service-centric-list-grid-icon"
                                icon={<Apps />}
                                onClick={() => {
                                  setRecentServicesView('grid')
                                }}
                                tip={t('dashboard:common.switch_to_grid_view')}
                              />
                              <Anchor
                                data-testid="my-service-link-list"
                                onClick={(e) => {
                                  e.preventDefault()
                                  navigate('/services/my-services')
                                }}
                                href="/services/my-services"
                              >
                                {t('common.my_services')}
                              </Anchor>
                            </Box>
                          ),
                          sortable: false,
                          render: (service) =>
                            isMSP() &&
                            service.workspaceTypes?.includes('TENANT') &&
                            !service.workspaceTypes?.includes('MSP') ? (
                              <span>
                                <Tag
                                  value={t(
                                    'dashboard:service_details.enabled_for_customer_workspaces'
                                  )}
                                  align="center"
                                  size="small"
                                  border={{ size: 'small', color: 'brand' }}
                                  color="background"
                                  style={{
                                    whiteSpace: 'nowrap'
                                  }}
                                />
                              </span>
                            ) : (
                              <Button
                                a11yTitle={`${t('common.launch')} ${
                                  service.name
                                }`}
                                href={service.staticLaunchUrl || null}
                                secondary
                                label={t('common.launch')}
                                onClick={() => {
                                  handleLaunch(
                                    service.staticLaunchUrl,
                                    service.serviceSlug,
                                    service.provision
                                  )
                                }}
                                data-testid="service-launch-list"
                                fill="horizontal"
                              />
                            )
                        }
                      ],
                      sort: {
                        direction: 'desc',
                        property: 'last_accessed'
                      },
                      data: (() => {
                        return recentServicesDataToUse
                          .filter(
                            (regions) =>
                              upsRecentServicesFlag ||
                              filterService(regions[0]?.slug)
                          )
                          .map((service) => {
                            const serviceName = upsRecentServicesFlag
                              ? service?.serviceOffer?.name
                              : service[0].name
                            const categories = upsRecentServicesFlag
                              ? service?.serviceOffer?.categories
                              : service[0].categories
                            const staticLaunchUrl = upsRecentServicesFlag
                              ? service?.serviceOffer?.staticLaunchUrl
                              : service[0].static_launch_url
                            const workspaceTypes = upsRecentServicesFlag
                              ? service?.serviceOffer?.workspaceTypes
                              : service[0]?.workspace_types
                            const serviceSlug = upsRecentServicesFlag
                              ? service?.serviceOffer?.slug
                              : service[0]?.slug
                            return {
                              provision: upsRecentServicesFlag
                                ? service
                                : [...service],
                              name: serviceName,
                              title: serviceName,
                              type: t(
                                `dashboard:common.category.${categoryMapper(
                                  categories
                                )}`
                              ),
                              last_accessed: upsRecentServicesFlag
                                ? service.lastAccessedTime
                                : service[0].utc,
                              serviceSlug,
                              staticLaunchUrl,
                              workspaceTypes,
                              path: '#'
                            }
                          })
                      })()
                    }}
                  />
                </Box>
              )}
            </Box>
          ))}
        {errorMessage && (
          <Notification
            backgroundColor="status-critical"
            icon={<StatusCritical color="text-strong" />}
            onClose={() => setErrorMessage('')}
            position="top"
            testId="service-centric-home-error-notification"
            text={errorMessage}
          />
        )}
      </Box>
    </VisibilityWrapper>
  )
}

export default NewRecentServices
